import React, { useEffect, useRef } from 'react';
import { IonButton, IonIcon } from '@ionic/react';
import { lockClosedOutline } from 'ionicons/icons';
import {
  useActionGuard,
  HelpText,
  UnlockFlow,
  extractBracketedText,
  stripBracketedText,
} from '@/hooks/useActionGuard';
import { useActionGuardContainerContext } from '@/contexts/ActionGuardContainerContext';

/**
 * What a locked guard hands its `fallbackElem` render function: everything needed
 * to explain the lock and offer a way out, already resolved across both axes
 * (verification claims and client-side extra claims) by `useActionGuard`.
 */
export interface ActionGuardLock {
  /** The lock's explanation, with any `[bracketed]` CTA label removed. */
  helpText: string;
  /** Label for the unlock CTA — the help text's `[bracketed]` text, if any. */
  buttonText: string | null;
  /** The flow that clears this lock; `navigateToUnlock` already targets it. */
  unlockFlow: UnlockFlow;
  /** Label for the "return here" button the unlock destination may render. */
  returnFromUnlockText?: string;
  /**
   * False when no flow can ever clear this lock (a permission the user can't
   * attain, not a verification they haven't done yet). `unlockFlow` is then a
   * formality and its CTA leads nowhere — render the copy without one.
   */
  isUnlockable: boolean;
  /** Sends the user to the flow that clears this lock. */
  navigateToUnlock: () => void;
}

export interface ActionGuardProps {
  disableMode: 'locked' | 'hidden' | 'on-demand';
  action: string;
  resource: string;
  guardedElem: React.ReactElement;
  /**
   * Copy shown when the action is locked (tooltip in `locked` mode, toast in
   * `on-demand` mode). Optional — omit it for a `hidden`-mode guard that just
   * shows/hides an element with no unlock affordance to surface the text.
   */
  helpText?: HelpText;
  resourceId?: string;
  /**
   * In `hidden` mode (or `on-demand` when the action is not unlockable at all),
   * rendered in place of the guarded element when the user lacks permission
   * (instead of rendering nothing). Pass a function to receive the resolved
   * `ActionGuardLock` — the copy, CTA label and unlock destination for whichever
   * axis is actually blocking — so the fallback describes the real lock instead
   * of restating one case of it.
   */
  fallbackElem?:
    | React.ReactElement
    | ((lock: ActionGuardLock) => React.ReactElement);
  /**
   * `hidden` mode only. While the permission check is still resolving, render
   * `guardedElem` optimistically instead of `fallbackElem`, then correct to the
   * fallback only if the check comes back denied. Use when denial is the rare
   * case (e.g. a user who already passed an upstream gate) and the fallback
   * would otherwise flash on every entry before permissions load. An unmet
   * extra claim denies the action without the check, so it shows the fallback
   * even while the check resolves.
   */
  optimistic?: boolean;
  /**
   * `on-demand` mode only. A consolation shown when the action is locked and no
   * verification path can ever unlock it (`isUnlockable` is false) — instead of
   * hiding the button, it renders **enabled** and a tap presents a toast. Either:
   *  - a plain message `string` → a buttonless toast with that message; or
   *  - a `[message, route]` tuple → the toast additionally carries a button that
   *    navigates to `route` for more info / customer service. Its label is the
   *    `[bracketed]` text in `message`, defaulting to "Go" when there is none.
   * Use for gates the user cannot self-clear (e.g. beta-tester-only action
   * creation). Takes precedence over `fallbackElem` in that state.
   */
  onDemandConsolation?: string | [message: string, route: string];
  /**
   * Names this guard's place on the "return here" button an unlock destination
   * renders, in place of the label the lock supplies. The lock's label assumes
   * the user was mid-action (e.g. "Back to sharing"); a guard on the button that
   * starts the action says where the user actually returns to (e.g. "Back to
   * topic"). Used only when the lock offers a return button at all.
   */
  returnLabel?: string;
  /**
   * `on-demand` mode only. Runs when the toast's unlock button is tapped, and is
   * awaited before the user is sent to `unlockFlow`. A caller that wants the
   * unlock destination's "return here" button to land somewhere other than the
   * current page pushes that page here: the destination is pushed on top of it,
   * so its return (a `goBack`) lands there.
   */
  onUnlock?: (unlockFlow: UnlockFlow) => void | Promise<void>;
}

/**
 * ActionGuard component that wraps buttons/elements and controls their display
 * based on permission status. Shows disabled button with lock icon or hides entirely.
 * When `resourceId` is supplied, the check uses scoped Casbin permissions for that ID.
 * In `hidden` mode, an optional `fallbackElem` can be shown when permission is denied.
 */
export const ActionGuard: React.FC<ActionGuardProps> = ({
  disableMode,
  action,
  resource,
  guardedElem,
  helpText,
  resourceId,
  fallbackElem,
  optimistic,
  onDemandConsolation,
  returnLabel,
  onUnlock,
}) => {
  const containerContext = useActionGuardContainerContext();
  const childIdRef = useRef<string>(`${action}_${resource}_${Date.now()}`);

  if (!containerContext) {
    throw new Error(
      'ActionGuard must be wrapped in an ActionGuardContainer component'
    );
  }

  const {
    canPerformAction,
    isUnlockable,
    claimsThatWouldUnlock: verifThatWouldUnlock,
    extraClaimUnlock,
    helpText: displayHelpText,
    unlockFlow,
    returnFromUnlockText: lockReturnText,
    isLoading,
  } = useActionGuard(
    action,
    resource,
    helpText ?? { singleVerification: '', updateProfile: '', setupProfile: '' },
    resourceId
  );
  const returnFromUnlockText =
    lockReturnText && returnLabel ? returnLabel : lockReturnText;

  // Register with container context
  useEffect(() => {
    const childId = childIdRef.current;
    containerContext.registerChild({
      id: childId,
      action,
      resource,
      canPerformAction,
      isUnlockable,
      claimsThatWouldUnlock: verifThatWouldUnlock,
      extraClaimUnlock,
      isLoading,
    });

    return () => {
      containerContext.unregisterChild(childId);
    };
  }, [
    containerContext,
    action,
    resource,
    canPerformAction,
    isUnlockable,
    verifThatWouldUnlock,
    extraClaimUnlock,
    isLoading,
  ]);

  // Update registration when status changes
  useEffect(() => {
    containerContext.registerChild({
      id: childIdRef.current,
      action,
      resource,
      canPerformAction,
      isUnlockable,
      claimsThatWouldUnlock: verifThatWouldUnlock,
      extraClaimUnlock,
      isLoading,
    });
  }, [
    containerContext,
    action,
    resource,
    canPerformAction,
    isUnlockable,
    verifThatWouldUnlock,
    extraClaimUnlock,
    isLoading,
  ]);

  // Resolve `fallbackElem` for the current lock. An element is rendered as given;
  // a function is handed the lock so the fallback can render the guard's own copy
  // and CTA rather than duplicating them.
  const renderFallback = (): React.ReactElement | null => {
    if (!fallbackElem) return null;
    if (typeof fallbackElem !== 'function') return fallbackElem;
    return fallbackElem({
      helpText: stripBracketedText(displayHelpText),
      buttonText: extractBracketedText(displayHelpText),
      unlockFlow,
      returnFromUnlockText,
      isUnlockable,
      navigateToUnlock: () =>
        containerContext.navigateToUnlock(unlockFlow, returnFromUnlockText),
    });
  };

  // Locked Mode - show disabled button with lock icon
  if (disableMode === 'locked') {
    if (canPerformAction) {
      // Action is available - render guarded element as-is
      return <>{guardedElem}</>;
    }

    // Strip any [bracketed] action text — that syntax is for toast buttons, not tooltips
    const lockTooltip = stripBracketedText(displayHelpText);

    if (isUnlockable) {
      // Action is locked but unlockable - show disabled button + lock icon
      const disabledGuardedElem = React.cloneElement(guardedElem, {
        disabled: true,
      } as React.HTMLAttributes<HTMLElement>);

      return (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>{disabledGuardedElem}</div>
            <IonButton
              fill="clear"
              size="small"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                containerContext.navigateToUnlock(unlockFlow);
              }}
              style={{ minWidth: 'auto', padding: '8px' }}
              aria-label={lockTooltip}
              title={lockTooltip}
              data-testid="action-guard-lock-button"
            >
              <IonIcon icon={lockClosedOutline} />
            </IonButton>
          </div>
        </>
      );
    }

    // Action is locked and not unlockable - just show disabled button
    const disabledGuardedElem = React.cloneElement(guardedElem, {
      disabled: true,
    } as React.HTMLAttributes<HTMLElement>);

    return <>{disabledGuardedElem}</>;
  }

  // Hidden Mode - hide button entirely when locked
  if (disableMode === 'hidden') {
    if (canPerformAction) {
      // Action is available - render guarded element as-is
      return <>{guardedElem}</>;
    }

    // Optimistic: while the permission check is still resolving, show the guarded
    // content rather than flashing the fallback. Once resolved it either stays
    // (if allowed) or corrects to the fallback below (if denied). An unmet extra
    // claim (e.g. no Familiar Name) is known without the check and denies the
    // action whatever it returns, so it goes straight to the fallback: guarded
    // content mounted for that moment could start work of its own, like opening
    // a sheet.
    if (optimistic && isLoading && !extraClaimUnlock) {
      return <>{guardedElem}</>;
    }

    // Action is locked - render the fallback element if provided, otherwise hide
    // the guarded element entirely (with or without unlock ability).
    // Note: modals are not rendered here because there's no UI to trigger them
    return <>{renderFallback()}</>;
  }

  // On-demand Mode - show button enabled, but intercept click to show toast
  if (disableMode === 'on-demand') {
    if (canPerformAction) {
      return <>{guardedElem}</>;
    }

    // While permissions are still loading, render the button disabled so a
    // click can't be mistakenly intercepted before the user's real permissions
    // are known. Once loaded, the click is intercepted into a toast.
    if (isLoading) {
      const loadingElem = React.cloneElement(guardedElem, {
        disabled: true,
      } as React.HTMLAttributes<HTMLElement>);
      return <>{loadingElem}</>;
    }

    // No verification path exists at any trust level. If a consolation was given,
    // the lock is one the user can't self-clear but can still be softened: render
    // the button enabled and present the consolation toast on click (e.g.
    // beta-tester-only creation → an invitation to the contact page). Otherwise
    // don't tease a button whose only behavior is a "you can't do this" toast
    // (e.g. a non-manager on a manager-only-share action) — show the fallback
    // notice if one was given.
    if (!isUnlockable) {
      if (onDemandConsolation !== undefined) {
        const [message, route] = Array.isArray(onDemandConsolation)
          ? onDemandConsolation
          : [onDemandConsolation, undefined];
        const consolationElem = React.cloneElement(guardedElem, {
          onClick: (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            containerContext.triggerConsolationToast(message, route);
          },
        } as React.HTMLAttributes<HTMLElement>);
        return <>{consolationElem}</>;
      }
      return <>{renderFallback()}</>;
    }

    const onDemandElem = React.cloneElement(guardedElem, {
      onClick: (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        containerContext.triggerToast(
          unlockFlow,
          displayHelpText,
          returnFromUnlockText,
          onUnlock
        );
      },
    } as React.HTMLAttributes<HTMLElement>);

    return <>{onDemandElem}</>;
  }

  // Fallback - should never reach here
  return <>{guardedElem}</>;
};
